(() => {
  const root = document.documentElement;
  const currency = root.dataset.roamunoCurrency;
  const country = root.dataset.roamunoCountry;
  const localeRoot = window.Shopify?.routes?.root || '/';
  const cache = new Map();
  let invalidated = false;
  let inFlightContext;
  const rootMatches = () => !invalidated && root.dataset.roamunoCurrency === currency && root.dataset.roamunoCountry === country;
  const invalidate = () => {
    invalidated = true;
    cache.clear();
    document.querySelectorAll('[data-roamuno-market-price]').forEach(price => { price.hidden = true; });
  };
  const read = async path => {
    const response = await fetch(path, {credentials:'same-origin',cache:'no-store',signal:AbortSignal.timeout(5000)});
    if (!response.ok) throw new Error('market_price_unavailable');
    return response;
  };
  // Ajax prices are native Shopify hundredths (including JPY), not Admin minor
  // units. Verify the same session's actual cart denomination before display.
  // Only outstanding requests are shared. Read fresh Liquid session country as
  // well as cart denomination before/after every quote consumption, even cached.
  const current = async (forceNew = false) => {
    if (!rootMatches()) { invalidate(); throw new Error('market_context_changed'); }
    if (forceNew || !inFlightContext) inFlightContext = (async () => {
      const [cart, html] = await Promise.all([
        read(`${localeRoot}cart.js`).then(response => response.json()),
        read(`${localeRoot}?section_id=roamuno-market-context`).then(response => response.text()),
      ]);
      const context = new DOMParser().parseFromString(html,'text/html').querySelector('[data-roamuno-market-context]');
      if (!context || cart.currency !== currency || context.dataset.currency !== currency || context.dataset.country !== country || !rootMatches()) throw new Error('market_context_changed');
    })();
    const pending = inFlightContext;
    try {
      await pending;
      if (!rootMatches()) throw new Error('market_context_changed');
    } catch (error) {
      invalidate();
      throw error;
    } finally {
      if (inFlightContext === pending) inFlightContext = undefined;
    }
  };
  const verifyOnReturn = () => {
    const visible = [...document.querySelectorAll('[data-roamuno-market-price]')].filter(price => !price.hidden);
    visible.forEach(price => { price.hidden = true; });
    current(true).then(() => {
      if (rootMatches()) visible.filter(price => price.isConnected).forEach(price => { price.hidden = false; });
    }).catch(() => {});
  };
  window.addEventListener('focus', verifyOnReturn);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') verifyOnReturn();
  });
  window.ROAMUNO_MARKET_PRICES = {
    async minimum(handle, skus = []) {
      try {
        if (!/^[a-z0-9-]+$/.test(handle) || !Array.isArray(skus)) return null;
        await current();
        const key = `${country}:${currency}:${handle}:${[...skus].sort().join(',')}`;
        if (!cache.has(key)) cache.set(key, (async () => {
          const product = await (await read(`${localeRoot}products/${handle}.js`)).json();
          const isAllowed = window.ROAMUNO_LAUNCH_ELIGIBILITY?.isAllowedVariantId;
          if (!isAllowed || !Array.isArray(product.variants)) return null;
          const prices = product.variants.filter(v => v.available === true && isAllowed(v.id)
            && (!skus.length || skus.includes(v.sku)) && Number.isSafeInteger(v.price) && v.price >= 0).map(v => v.price / 100);
          if (!prices.length || root.dataset.roamunoCurrency !== currency || root.dataset.roamunoCountry !== country) return null;
          return {currency,amount:Math.min(...prices)};
        })());
        const quote = await cache.get(key);
        // Start this read after the product/cache result; an older outstanding
        // request must not stand in for evidence obtained after that result.
        await current(true);
        return rootMatches() ? quote : null;
      } catch {
        // Missing live market prices leave the price claim absent. Never format
        // static USD coverage prices with another currency symbol.
        return null;
      }
    },
  };
})();
